/**
 * Node Palette - Icon Library & Shape Library Integration
 * Shows all 1,487 icons organized by provider + 50 shapes organized by category
 */

import { memo, useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Image, Shapes, ChevronsDown, ChevronsUp, Box, Database, ChevronRight, Star } from 'lucide-react';
import '../../styles/node-palette.css';
import {
  getAllProviders,
  getIcon,
  getIconsByProvider,
  searchIcons,
  PROVIDER_METADATA,
  type IconProvider,
  type IconDefinition,
} from '../../config/icons';
import {
  getAllCategories,
  getShape,
  getShapesByCategory,
  searchShapes,
  CATEGORY_METADATA,
  type ShapeCategory,
  type ShapeDefinition,
} from '../../config/shapes';
import type { DiagramType, NodeType } from '../../types/schema';

interface NodePaletteProps {
  diagramType: DiagramType;
  onAddNode: (nodeType: NodeType, iconData?: IconDefinition, shapeData?: ShapeDefinition, technicalType?: 'database-table' | 'api-resource' | 'api-endpoint' | 'uml-class' | 'state-node' | 'c4-node') => void;
  onClose?: () => void;
}

type TabType = 'icons' | 'shapes' | 'technical';

const FAVORITES_STORAGE_KEY = 'gdg-palette-favorites';

// Favorites are namespaced as "icon:<id>" / "shape:<id>" in a single Set.
function loadFavorites(): Set<string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(FAVORITES_STORAGE_KEY) : null;
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    // ignore malformed storage
  }
  return new Set();
}

function NodePalette({ diagramType, onAddNode, onClose }: NodePaletteProps) {
  const [activeTab, setActiveTab] = useState<TabType>('icons');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavorites());
  const [favoritesExpanded, setFavoritesExpanded] = useState(true);
  const [expandedProviders, setExpandedProviders] = useState<Set<IconProvider>>(
    new Set(['aws']) // AWS expanded by default
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<ShapeCategory>>(
    new Set(['basic']) // Basic shapes expanded by default
  );
  const [width, setWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);

  const providers = getAllProviders();
  const shapeCategories = getAllCategories();

  // Handle resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(Math.max(e.clientX, 200), 600); // Min 200px, max 600px
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Persist favorites to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)));
    } catch {
      // ignore storage write failures (e.g. private mode / quota)
    }
  }, [favorites]);

  // Toggle a favorite without adding the node or starting a drag
  const toggleFavorite = useCallback((favKey: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(favKey)) {
        next.delete(favKey);
      } else {
        next.add(favKey);
      }
      return next;
    });
  }, []);

  // Resolve favorite keys back into definitions for the active tab
  const favoriteIcons = useMemo(() => {
    const result: IconDefinition[] = [];
    favorites.forEach((key) => {
      if (key.startsWith('icon:')) {
        const icon = getIcon(key.slice('icon:'.length));
        if (icon) result.push(icon);
      }
    });
    return result;
  }, [favorites]);

  const favoriteShapes = useMemo(() => {
    const result: ShapeDefinition[] = [];
    favorites.forEach((key) => {
      if (key.startsWith('shape:')) {
        const shape = getShape(key.slice('shape:'.length));
        if (shape) result.push(shape);
      }
    });
    return result;
  }, [favorites]);

  // Toggle provider section
  const toggleProvider = (provider: IconProvider) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) {
        next.delete(provider);
      } else {
        next.add(provider);
      }
      return next;
    });
  };

  // Toggle shape category section
  const toggleCategory = (category: ShapeCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Expand all providers/categories
  const expandAll = () => {
    if (activeTab === 'icons') {
      setExpandedProviders(new Set(providers));
    } else {
      setExpandedCategories(new Set(shapeCategories));
    }
  };

  // Collapse all providers/categories
  const collapseAll = () => {
    if (activeTab === 'icons') {
      setExpandedProviders(new Set());
    } else {
      setExpandedCategories(new Set());
    }
  };

  // Filter icons by search query
  const filteredProviders = useMemo(() => {
    if (!searchQuery.trim()) {
      // No search - show ALL icons for each provider
      return providers.map((provider) => ({
        provider,
        icons: getIconsByProvider(provider), // Show ALL icons (no limit!)
      }));
    }

    // Search across all icons
    const searchResults = searchIcons(searchQuery);

    // Group by provider
    const grouped = new Map<IconProvider, IconDefinition[]>();
    searchResults.forEach((icon) => {
      if (!grouped.has(icon.provider)) {
        grouped.set(icon.provider, []);
      }
      grouped.get(icon.provider)!.push(icon);
    });

    // Convert to array - show ALL search results
    return Array.from(grouped.entries()).map(([provider, icons]) => ({
      provider,
      icons: icons, // Show ALL search results (no limit!)
    }));
  }, [searchQuery, providers]);

  // Filter shapes by search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      // No search - show ALL shapes for each category
      return shapeCategories.map((category) => ({
        category,
        shapes: getShapesByCategory(category),
      }));
    }

    // Search across all shapes
    const searchResults = searchShapes(searchQuery);

    // Group by category
    const grouped = new Map<ShapeCategory, ShapeDefinition[]>();
    searchResults.forEach((shape) => {
      if (!grouped.has(shape.category)) {
        grouped.set(shape.category, []);
      }
      grouped.get(shape.category)!.push(shape);
    });

    // Convert to array
    return Array.from(grouped.entries()).map(([category, shapes]) => ({
      category,
      shapes: shapes,
    }));
  }, [searchQuery, shapeCategories]);

  // Auto-expand providers/categories that have search results
  useEffect(() => {
    if (searchQuery.trim()) {
      if (activeTab === 'icons') {
        // Search and find which providers have results
        const searchResults = searchIcons(searchQuery);
        const providersWithResults = new Set(
          searchResults.map((icon) => icon.provider)
        );
        setExpandedProviders(providersWithResults);
      } else {
        // Search and find which categories have results
        const searchResults = searchShapes(searchQuery);
        const categoriesWithResults = new Set(
          searchResults.map((shape) => shape.category)
        );
        setExpandedCategories(categoriesWithResults);
      }
    }
  }, [searchQuery, activeTab]);

  const handleAddNode = (icon: IconDefinition) => {
    // Add node with icon data
    onAddNode('component' as NodeType, icon);
  };

  const handleAddShape = (shape: ShapeDefinition) => {
    // Add node with shape data
    onAddNode('component' as NodeType, undefined, shape);
  };

  // Star toggle rendered inside each card. Uses a span (not a button) because
  // the card itself is a <button> and nested buttons are invalid HTML.
  // stopPropagation + preventDefault keep it from adding a node or starting a drag.
  const renderFavStar = (favKey: string) => {
    const isFav = favorites.has(favKey);
    const stop = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={isFav}
        title={isFav ? 'Remove from favorites' : 'Add to favorites'}
        className={`palette-card-fav${isFav ? ' is-fav' : ''}`}
        draggable={false}
        onDragStart={stop}
        onPointerDown={stop}
        onMouseDown={stop}
        onClick={(e) => {
          stop(e);
          toggleFavorite(favKey);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            stop(e);
            toggleFavorite(favKey);
          }
        }}
      >
        <Star size={13} />
      </span>
    );
  };

  // Reusable card renderers (also used by the Favorites section)
  const renderIconCard = (icon: IconDefinition) => (
    <button
      key={icon.id}
      onClick={() => handleAddNode(icon)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/reactflow-icon', JSON.stringify(icon));
        e.dataTransfer.effectAllowed = 'move';
      }}
      title={`${icon.name}\nClick to add or drag to canvas`}
      className="palette-card"
    >
      {renderFavStar(`icon:${icon.id}`)}
      <span className="palette-card-thumb">
        <img src={icon.path} alt={icon.name} />
      </span>
      <span className="palette-card-name">{icon.name}</span>
    </button>
  );

  const renderShapeCard = (shape: ShapeDefinition) => (
    <button
      key={shape.id}
      onClick={() => handleAddShape(shape)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/reactflow-shape', JSON.stringify(shape));
        e.dataTransfer.effectAllowed = 'move';
      }}
      title={`${shape.name}\nClick to add or drag to canvas`}
      className="palette-card"
    >
      {renderFavStar(`shape:${shape.id}`)}
      <span className="palette-card-thumb">
        <img src={shape.path} alt={shape.name} />
      </span>
      <span className="palette-card-name">{shape.name}</span>
    </button>
  );

  // Collapsible Favorites section at the top of a tab
  const renderFavoritesGroup = (count: number, children: React.ReactNode) => (
    <div className="palette-group">
      <button
        onClick={() => setFavoritesExpanded((v) => !v)}
        className="palette-group-header"
        aria-expanded={favoritesExpanded}
        style={{ ['--accent']: '#f59e0b' } as React.CSSProperties}
      >
        <span className="palette-group-left">
          <ChevronRight size={14} className="palette-group-chevron" />
          <Star size={13} style={{ color: '#f59e0b', fill: '#f59e0b' }} />
          <span className="palette-group-name">Favorites</span>
        </span>
        <span className="palette-group-count">{count}</span>
      </button>
      {favoritesExpanded && <div className="palette-card-grid">{children}</div>}
    </div>
  );

  return (
    <div
      className="node-palette"
      style={{
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      }}
    >
      {/* Header */}
      <div className="node-palette-header">
        <div className="node-palette-header-top">
          <h3 className="node-palette-title">
            <Box size={18} />
            Node Library
          </h3>
          {onClose && (
            <button onClick={onClose} className="close-palette-btn">
              ×
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="palette-tabs">
          <button
            onClick={() => {
              setActiveTab('icons');
              setSearchQuery('');
            }}
            className={`palette-tab ${activeTab === 'icons' ? 'active' : ''}`}
          >
            <Image size={16} />
            <span className="palette-tab-label">Icons</span>
            <span className="palette-tab-count">(1,487)</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('shapes');
              setSearchQuery('');
            }}
            className={`palette-tab ${activeTab === 'shapes' ? 'active' : ''}`}
          >
            <Shapes size={16} />
            <span className="palette-tab-label">Shapes</span>
            <span className="palette-tab-count">(41)</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('technical');
              setSearchQuery('');
            }}
            className={`palette-tab ${activeTab === 'technical' ? 'active' : ''}`}
          >
            <Database size={16} />
            <span className="palette-tab-label">Technical</span>
            <span className="palette-tab-count">(3)</span>
          </button>
        </div>

        {/* Search */}
        <div className="palette-search-wrapper">
          <Search size={16} className="palette-search-icon" />
          <input
            type="text"
            className="palette-search"
            placeholder={
              activeTab === 'icons'
                ? 'Search icons...'
                : activeTab === 'shapes'
                ? 'Search shapes...'
                : 'Search technical nodes...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Expand/Collapse All */}
        {!searchQuery && (
          <div className="palette-controls">
            <button onClick={expandAll} className="palette-control-btn">
              <ChevronsDown size={14} />
              Expand All
            </button>
            <button onClick={collapseAll} className="palette-control-btn">
              <ChevronsUp size={14} />
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Content - Icons, Shapes, or Technical */}
      <div className="node-palette-content">
        {activeTab === 'icons' ? (
          // ICONS TAB
          filteredProviders.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#9ca3af',
              }}
            >
              <p style={{ marginBottom: '8px' }}>No icons found</p>
              <p style={{ fontSize: '12px' }}>
                Try searching for "compute", "database", or "lambda"
              </p>
            </div>
          ) : (
            <>
            {favoriteIcons.length > 0 &&
              renderFavoritesGroup(favoriteIcons.length, favoriteIcons.map(renderIconCard))}
            {filteredProviders.map(({ provider, icons }) => {
            const isExpanded = expandedProviders.has(provider);
            const metadata = PROVIDER_METADATA[provider];

            return (
              <div key={provider} className="palette-group">
                {/* Provider Header - Clickable to expand/collapse */}
                <button
                  onClick={() => toggleProvider(provider)}
                  className="palette-group-header"
                  aria-expanded={isExpanded}
                  style={{ ['--accent']: metadata.color } as React.CSSProperties}
                >
                  <span className="palette-group-left">
                    <ChevronRight size={14} className="palette-group-chevron" />
                    <span className="palette-group-swatch" />
                    <span className="palette-group-name">{metadata.name}</span>
                  </span>
                  <span className="palette-group-count">
                    {icons.length}{searchQuery ? ' matches' : ''}
                  </span>
                </button>

                {/* Icons Grid - Only show when expanded */}
                {isExpanded && (
                  <div className="palette-card-grid">
                    {icons.map(renderIconCard)}
                  </div>
                )}
              </div>
            );
          })}
          </>
        )) : activeTab === 'shapes' ? (
          // SHAPES TAB
          filteredCategories.length === 0 ? (
            <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#9ca3af',
            }}
          >
            <p style={{ marginBottom: '8px' }}>No shapes found</p>
            <p style={{ fontSize: '12px' }}>
              Try searching for "circle", "arrow", or "flowchart"
            </p>
          </div>
        ) : (
            <>
            {favoriteShapes.length > 0 &&
              renderFavoritesGroup(favoriteShapes.length, favoriteShapes.map(renderShapeCard))}
            {filteredCategories.map(({ category, shapes }) => {
              const isExpanded = expandedCategories.has(category);
              const metadata = CATEGORY_METADATA[category];

              return (
                <div key={category} className="palette-group">
                  {/* Category Header - Clickable to expand/collapse */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="palette-group-header"
                    aria-expanded={isExpanded}
                    style={{ ['--accent']: metadata.color } as React.CSSProperties}
                  >
                    <span className="palette-group-left">
                      <ChevronRight size={14} className="palette-group-chevron" />
                      <span className="palette-group-swatch" />
                      <span className="palette-group-name">{metadata.name}</span>
                    </span>
                    <span className="palette-group-count">
                      {shapes.length}{searchQuery ? ' matches' : ''}
                    </span>
                  </button>

                  {/* Shapes Grid - Only show when expanded */}
                  {isExpanded && (
                    <div className="palette-card-grid">
                      {shapes.map(renderShapeCard)}
                    </div>
                  )}
                </div>
              );
            })}
            </>
        )) : (
          // TECHNICAL TAB
          <div className="palette-tech-list">
            {/* Database Table Node */}
            <button
              onClick={() => onAddNode('table' as NodeType, undefined, undefined, 'database-table')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'database-table' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="Database Table"
              className="palette-tech-card"
              style={{ ['--accent']: '#0891b2' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">🗄️</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">Database Table</div>
                <div className="palette-tech-desc">ERD table with columns and constraints</div>
              </div>
            </button>

            {/* API Resource Node */}
            <button
              onClick={() => onAddNode('api' as NodeType, undefined, undefined, 'api-resource')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'api-resource' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="API Resource"
              className="palette-tech-card"
              style={{ ['--accent']: '#22c55e' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">⚡</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">API Resource</div>
                <div className="palette-tech-desc">REST resource with methods</div>
              </div>
            </button>

            {/* API Endpoint Node */}
            <button
              onClick={() => onAddNode('api' as NodeType, undefined, undefined, 'api-endpoint')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'api-endpoint' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="API Endpoint"
              className="palette-tech-card"
              style={{ ['--accent']: '#8b5cf6' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">📡</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">API Endpoint</div>
                <div className="palette-tech-desc">Endpoint documentation with methods</div>
              </div>
            </button>

            {/* UML Class Node */}
            <button
              onClick={() => onAddNode('component' as NodeType, undefined, undefined, 'uml-class')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'uml-class' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="UML Class"
              className="palette-tech-card"
              style={{ ['--accent']: '#6366f1' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">🧩</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">UML Class</div>
                <div className="palette-tech-desc">Class box with attributes and methods</div>
              </div>
            </button>

            {/* State Machine Node */}
            <button
              onClick={() => onAddNode('node' as NodeType, undefined, undefined, 'state-node')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'state-node' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="State"
              className="palette-tech-card"
              style={{ ['--accent']: '#0ea5e9' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">🔵</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">State</div>
                <div className="palette-tech-desc">Statechart state with internal actions</div>
              </div>
            </button>

            {/* C4 Element Node */}
            <button
              onClick={() => onAddNode('component' as NodeType, undefined, undefined, 'c4-node')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/reactflow-technical', JSON.stringify({ type: 'c4-node' }));
                e.dataTransfer.effectAllowed = 'move';
              }}
              title="C4 Element"
              className="palette-tech-card"
              style={{ ['--accent']: '#1168bd' } as React.CSSProperties}
            >
              <span className="palette-tech-icon">🏛️</span>
              <div className="palette-tech-text">
                <div className="palette-tech-title">C4 Element</div>
                <div className="palette-tech-desc">Person / system / container / component</div>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Footer - Hidden on mobile to save space */}
      <div
        className="node-palette-footer"
        style={{
          borderTop: '1px solid #e5e7eb',
          padding: '12px 16px',
          background: '#f9fafb',
          fontSize: '11px',
          color: '#6b7280',
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
          {activeTab === 'icons'
            ? `${filteredProviders.reduce((sum, p) => sum + p.icons.length, 0)} icons`
            : activeTab === 'shapes'
            ? `${filteredCategories.reduce((sum, c) => sum + c.shapes.length, 0)} shapes`
            : '3 technical nodes'}
          {searchQuery && activeTab !== 'technical' && ' found'}
        </div>
        <div style={{ opacity: 0.8 }}>
          Diagram: <span style={{ fontWeight: 'bold' }}>{diagramType}</span>
        </div>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className={`palette-resize-handle ${isResizing ? 'resizing' : ''}`}
      />
    </div>
  );
}

export default memo(NodePalette);
